import { useCallback, useEffect, useState } from "react";
import {
  clearDeviceSession,
  deviceApi,
  DeviceApiError,
  getDeviceSession,
  setDeviceSession,
  type DeviceSession,
  type PaymentMethod,
} from "../../infrastructure/deviceApi";
import { isDemoCode, maybeStartDemo, startDemo } from "../../infrastructure/demo";

// El demo se arranca ANTES de leer la sesión, para que el shim de red ya esté instalado
// cuando el primer hook pida datos. Mismo orden que tenía el PIN mágico en v1.
const _demoBoot = maybeStartDemo();

export function useDeviceSession() {
  const [session, setSession] = useState<DeviceSession | null>(() => getDeviceSession());
  const [methods, setMethods] = useState<PaymentMethod[] | null>(null);
  const [loading, setLoading] = useState(true);

  // Al arrancar en demo, la sesión la crea el propio shim: canjeamos el código de demo una
  // sola vez para que /demo funcione como deep-link compartible, sin pasar por la pantalla.
  useEffect(() => {
    if (!_demoBoot || getDeviceSession()) return;
    deviceApi.redeem("demo", "Demo web").then((r) => {
      setDeviceSession(r.device_token, r.terminal, r.tenant);
      setSession(getDeviceSession());
    });
  }, []);

  const refreshMethods = useCallback(async () => {
    if (!getDeviceSession()) {
      setMethods(null);
      setLoading(false);
      return;
    }
    try {
      const me = await deviceApi.me();
      setMethods(Array.isArray(me.payment_methods) ? me.payment_methods : []);
    } catch {
      // Fail-closed: sin respuesta no declaramos Lightning. Un 401 ya redirigió al pareo.
      setMethods([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMethods();
  }, [refreshMethods, session?.token]);

  const pair = useCallback(
    async (code: string, deviceName?: string): Promise<string | null> => {
      const limpio = code.trim();
      if (!limpio) return "Escribí el código que te da el panel.";
      // El código de demo no toca la red: arranca el shim y después canjea contra él.
      if (isDemoCode(limpio)) startDemo();
      try {
        const r = await deviceApi.redeem(limpio, deviceName || "POS web");
        setDeviceSession(r.device_token, r.terminal, r.tenant);
        setSession(getDeviceSession());
        return null;
      } catch (e) {
        return mensajeDePareo(e);
      }
    },
    []
  );

  const unpair = useCallback(() => {
    clearDeviceSession();
    setSession(null);
    setMethods(null);
  }, []);

  return {
    session,
    paired: !!session,
    loading,
    // El efectivo no depende de ningún proveedor externo: siempre se puede cobrar.
    cash: true,
    // Fail-closed, igual que la app: si no sabemos, NO ofrecemos bitcoin. Prometer un cobro
    // que después falla es peor que no ofrecerlo.
    lightning: Array.isArray(methods) ? methods.includes("lightning") : false,
    pair,
    unpair,
    refreshMethods,
  };
}

/** El cajero tiene que poder distinguir "escribiste mal el código" de "la red se cayó". */
function mensajeDePareo(e: unknown): string {
  const code = e instanceof DeviceApiError ? e.code : "";
  if (code === "pairing_invalid_or_expired") {
    return "Ese código no sirve o ya venció. Generá uno nuevo en el panel (duran 90 segundos).";
  }
  if (code === "network_error") {
    return "No se pudo conectar. Revisá la conexión e intentá de nuevo.";
  }
  if (code.startsWith("http_429")) {
    return "Demasiados intentos. Esperá un momento y probá otra vez.";
  }
  return "No se pudo emparejar la terminal. Intentá de nuevo.";
}
